import React, { useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import i18n from '../../../i18n';
import { GRID_COLUMN_DRAG_ATTR } from '../../../utils/gridColumnDrag';
import { removeGhostSx, removeZoneSx } from './GridColumnRemoveZone.styles';

const ZONE_TEXT = {
    idle: 'common.columnRemoveZone',
    hot: 'common.columnRemoveZoneHot',
    locked: 'common.columnRemoveZoneLocked'
};

// What a cell copy takes over from the cell as the table draws it: the
// density and the head's look are styled through the table around the
// cells, which the copy no longer sits in.
const COPIED_STYLES = [
    'backgroundColor',
    'color',
    'font',
    'padding',
    'textAlign',
    'verticalAlign'
];

// A copy of a table cell to draw the carried column with: the drag's marks
// and offset and the head cell's own controls (resize edge) left behind.
const copyCell = (cell) => {
    const copy = cell.cloneNode(true);
    const computed = getComputedStyle(cell);
    COPIED_STYLES.forEach((name) => {
        copy.style[name] = computed[name];
    });
    copy.removeAttribute(GRID_COLUMN_DRAG_ATTR);
    copy.removeAttribute('id');
    copy.style.transform = '';
    copy.style.transition = '';
    copy.querySelectorAll('[role="separator"], [id]').forEach((node) => {
        if (node.getAttribute('role') === 'separator') node.remove();
        else node.removeAttribute('id');
    });
    return copy;
};

/**
 * The remove zone over the top of the table while a column is carried up to
 * it (see `removal` in gridColumnDrag.js), and the carried column itself —
 * its head and first cells, copied from the table, following the pointer.
 * Presentation only: the drag decides where the zone goes and what a
 * release does. Its text is announced to screen readers as it changes.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.channel - From `createRemoveZoneChannel`: the zone's
 *   state, `null` while it is closed.
 * @param {Function} props.getColumnTitle - `(id)` → the column's title.
 * @returns {JSX.Element|null} the zone, portalled to the body
 */
export default function GridColumnRemoveZone({ channel, getColumnTitle }) {
    const state = useSyncExternalStore(channel.subscribe, channel.get);
    const ghostRef = useRef(null);
    const cells = state?.cells;

    // The copies are made once per carried column, not on every move.
    useLayoutEffect(() => {
        const ghost = ghostRef.current;
        if (!ghost || !cells) return;
        const table = document.createElement('table');
        const head = table.createTHead().insertRow();
        const body = table.createTBody();
        cells.forEach((cell, index) => {
            const row = index === 0 ? head : body.insertRow();
            row.appendChild(copyCell(cell));
        });
        ghost.replaceChildren(table);
    }, [cells]);

    if (!state) return null;

    const { phase, zone, pointer, grab, width } = state;
    const title = getColumnTitle(state.id);

    return createPortal(
        <>
            <Box
                role="status"
                data-phase={phase}
                data-testid="grid-column-remove-zone"
                sx={removeZoneSx}
                style={{
                    left: zone.left,
                    top: zone.top,
                    width: zone.width,
                    height: zone.height
                }}
            >
                {phase === 'locked' ? <LockOutlinedIcon /> : <DeleteOutlineIcon />}
                <span>{i18n.t(ZONE_TEXT[phase], { column: title })}</span>
            </Box>
            <Box
                aria-hidden
                data-phase={phase}
                sx={removeGhostSx}
                style={{
                    width,
                    transform: `translate(${pointer.x - grab.x}px, ${
                        pointer.y - grab.y
                    }px)`
                }}
            >
                <div ref={ghostRef} />
            </Box>
        </>,
        document.body
    );
}

GridColumnRemoveZone.propTypes = {
    channel: PropTypes.shape({
        get: PropTypes.func.isRequired,
        subscribe: PropTypes.func.isRequired
    }).isRequired,
    getColumnTitle: PropTypes.func.isRequired
};
